import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Activity, Loader2, RefreshCw, UserCheck } from 'lucide-react';

const API = `${window.__BACKEND_URL__}/api`;
const box = { background: '#0F1923', border: '1px solid #2D3748' };

// Settings → QuickBooks Online: "Test connection" panel (Sep 2026).
export const QbConnectionTest = ({ token }) => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/qb/test-connection`, { headers: { Authorization: `Bearer ${token}` } });
      setResult(r.data);
    } catch (e) {
      setResult({ ok: false, message: e.response?.data?.detail || 'Test failed — please try again.' });
    } finally {
      setBusy(false);
    }
  };
  const color = result ? (result.ok ? '#00C896' : '#F59E0B') : '#94A3B8';
  return (
    <div className="mt-4 p-4 rounded-xl" style={box} data-testid="qb-test-connection-section">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-white">Connection health</h4>
          <p className="text-xs" style={{ color: '#94A3B8' }}>Pings QuickBooks with your saved credentials and shows when the token expires.</p>
        </div>
        <button onClick={run} disabled={busy} className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
          style={{ background: '#1E88E5', color: '#fff' }} data-testid="qb-test-connection-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Activity className="w-4 h-4" />} {busy ? 'Testing…' : 'Test connection'}
        </button>
      </div>
      {result && (
        <div className="mt-3 text-xs space-y-1" data-testid="qb-test-connection-result">
          <p className="font-semibold" style={{ color }} data-testid="qb-test-connection-message">{result.message}</p>
          {result.ok && (
            <div className="flex flex-wrap gap-x-4 gap-y-0.5" style={{ color: '#94A3B8' }}>
              <span data-testid="qb-test-token-left">Access token: {result.access_token_minutes_left != null ? `${result.access_token_minutes_left} min left (auto-renews)` : 'unknown'}</span>
              {result.refresh_token_valid_until && <span>Connection valid until {new Date(result.refresh_token_valid_until).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })} if unused</span>}
              <span>Latency {result.latency_ms} ms</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// One unmatched-employee error row → pick the QuickBooks employee to pin.
const EmployeeMapRow = ({ err, token, employees, onMapped }) => {
  const [choice, setChoice] = useState('');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!choice) return;
    setSaving(true);
    try {
      const emp = employees.find((e) => e.id === choice);
      const r = await axios.post(`${API}/qb/employee-map`,
        { user_id: err.worker_id || undefined, worker_name: err.worker_id ? undefined : err.entity_id, qb_employee_id: choice, qb_employee_name: emp?.display_name },
        { headers: { Authorization: `Bearer ${token}` } });
      onMapped(err, r.data);
    } catch (e) {
      onMapped(err, null, e.response?.data?.detail || 'Could not save mapping');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="mt-2 flex flex-col sm:flex-row gap-2" data-testid={`qb-employee-map-${err.id}`}>
      <select value={choice} onChange={(e) => setChoice(e.target.value)}
        className="flex-1 px-2 py-1.5 rounded-lg text-xs bg-[#1A2433] border border-[#2D3748] text-white" data-testid={`qb-employee-map-select-${err.id}`}>
        <option value="">Map “{err.entity_id}” to a QuickBooks employee…</option>
        {employees.map((e) => (
          <option key={e.id} value={e.id}>{e.display_name}{e.email ? ` — ${e.email}` : ''}{e.active ? '' : ' (inactive)'}</option>
        ))}
      </select>
      <button onClick={save} disabled={!choice || saving} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50"
        style={{ background: '#00C896', color: '#0F1923' }} data-testid={`qb-employee-map-save-${err.id}`}>
        <UserCheck className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save mapping'}
      </button>
    </div>
  );
};

// Sync-errors list with "Retry all" and inline employee mapping.
export const QbSyncErrors = ({ token, errors, setErrors, connected, toast }) => {
  const [show, setShow] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [employees, setEmployees] = useState(null);
  const hasEmployeeErrors = errors.some((e) => e.entity_type === 'employee');
  const hasJobErrors = errors.some((e) => e.entity_type === 'job');

  useEffect(() => {
    if (!show || !hasEmployeeErrors || employees !== null || !connected) return;
    axios.get(`${API}/qb/employees`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setEmployees(r.data.employees || []))
      .catch((e) => { setEmployees([]); toast({ title: 'Could not load QuickBooks employees', description: e.response?.data?.detail || 'Please try again.', variant: 'destructive' }); });
  }, [show, hasEmployeeErrors, employees, connected, token, toast]);

  const resolveError = async (id) => {
    await axios.post(`${API}/qb/sync-errors/${id}/resolve`, {}, { headers: { Authorization: `Bearer ${token}` } });
    setErrors((prev) => prev.filter((e) => e.id !== id));
  };

  const reload = async () => {
    const r = await axios.get(`${API}/qb/sync-errors`, { headers: { Authorization: `Bearer ${token}` } });
    setErrors(r.data || []);
  };

  const retryAll = async () => {
    setRetrying(true);
    try {
      const r = await axios.post(`${API}/qb/sync-errors/retry-all`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Retry finished', description: r.data.message });
      await reload();
    } catch (e) {
      toast({ title: 'Retry stopped', description: e.response?.data?.detail || 'Could not retry pushes.', variant: 'destructive' });
      await reload().catch(() => {});
    } finally {
      setRetrying(false);
    }
  };

  const onMapped = (err, data, errorMsg) => {
    if (!data) { toast({ title: 'Mapping failed', description: errorMsg, variant: 'destructive' }); return; }
    toast({ title: 'Employee mapped', description: `${data.message}. Push payroll again to sync their hours.` });
    setErrors((prev) => prev.filter((e) => !(e.entity_type === 'employee' && (e.worker_id === data.user_id || e.entity_id === data.worker_name))));
  };

  if (errors.length === 0) return null;
  return (
    <div data-testid="qb-sync-errors">
      <div className="flex items-center justify-between gap-2">
        <button onClick={() => setShow(!show)} className="text-xs font-medium flex items-center gap-1" style={{ color: '#FFA000' }} data-testid="qb-sync-errors-toggle">
          {errors.length} sync error{errors.length !== 1 ? 's' : ''} {show ? '(hide)' : '(show)'}
        </button>
        {hasJobErrors && connected && (
          <button onClick={retryAll} disabled={retrying} className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg disabled:opacity-50"
            style={{ background: 'rgba(30,136,229,0.15)', color: '#60A5FA', border: '1px solid rgba(30,136,229,0.4)' }} data-testid="qb-retry-all-btn">
            {retrying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} {retrying ? 'Retrying…' : 'Retry all failed pushes'}
          </button>
        )}
      </div>
      {show && (
        <div className="mt-2 space-y-2 max-h-[360px] overflow-y-auto">
          {errors.map((err) => {
            const expanded = expandedId === err.id;
            const msg = err.error_message || '';
            return (
              <div key={err.id} className="p-2.5 rounded-lg" style={box} data-testid={`qb-sync-error-${err.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <p className="text-xs text-white truncate flex-1 min-w-0" title={err.entity_id}>
                    <span className="font-semibold uppercase text-[10px]" style={{ color: '#FFA000' }}>{err.entity_type}</span>
                    <span className="ml-1.5 font-mono">{String(err.entity_id).slice(0, 36)}</span>
                    {err.count > 1 && <span className="ml-1.5 text-[10px]" style={{ color: '#64748B' }}>×{err.count}</span>}
                  </p>
                  <button onClick={() => resolveError(err.id)} className="text-[10px] px-2 py-0.5 rounded" style={{ color: '#00C896' }} data-testid={`qb-sync-error-dismiss-${err.id}`}>Dismiss</button>
                </div>
                <p className={`text-xs mt-1 whitespace-pre-wrap ${expanded ? '' : 'line-clamp-2'}`} style={{ color: '#94A3B8', wordBreak: 'break-word' }} data-testid={`qb-sync-error-message-${err.id}`}>{msg}</p>
                {msg.length > 140 && (
                  <button onClick={() => setExpandedId(expanded ? null : err.id)} className="text-[11px] mt-1 hover:underline" style={{ color: '#1E88E5' }} data-testid={`qb-sync-error-toggle-${err.id}`}>
                    {expanded ? 'Show less' : 'Show full error'}
                  </button>
                )}
                {err.entity_type === 'employee' && connected && (
                  employees === null ? <p className="text-[11px] mt-2" style={{ color: '#64748B' }}>Loading QuickBooks employees…</p>
                    : <EmployeeMapRow err={err} token={token} employees={employees} onMapped={onMapped} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
